import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  customRound,
  floatToFloatSec,
  getHMSToSec,
  operateStorageData,
  playMedia,
  stringTocamelCase,
} from "../_helpers/utils";
import {
  BRAND,
  COLLAGE,
  DOCUMENT_PLAY_DATA_STORAGE_KEY,
  PAGE_PLAY_DATA_STORAGE_KEY,
  STOCK,
  UPLOAD_VIDEO,
  VIDEO,
  FRAME,
} from "../constants/editor";
import { FADE_EFFECT_OBJ, FADE_INTERVEL_OBJ } from "../constants/previewPresent";

const useHTMLPlayer = ({ docPagesMeta, ready }) => {
  const isSeekingRef = useRef(false);
  const intervalId = useRef(null);
  const audioIntervalId = useRef(null);
  const globalAudioTimer = useRef(0);
  let animationSpeedToDurationMsObj = {
    Fast: 500,
    Medium: 1000,
    Slow: 2000,
  };
  const documentPlayObj = {
    action: null,
    pageId: null,
    pageIdx: null,
    documentTimer: null,
    documentProgress: 0,
    documentDurationMs: 0,
    pageArray: [],
  };
  const pagePlayObj = {
    action: null,
    pageId: null,
    pageIdx: null,
    pageTimer: null,
    pageProgress: 0,
    pageDurationMs: 0,
  };

  const TEXT_ANIMATION_PRESETS = {
    flipInX: {
      animationName: "flipInX",
      charClass: "flip-in-x",
      duration: 0.8,
      ease: "ease-out",
    },
    flipInY: {
      animationName: "flipInY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-out",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceIn: {
      animationName: "bounceIn",
      charClass: "bounce-in-letter",
      duration: 0.9,
    },
    rotateIn: {
      animationName: "rotateIn",
      charClass: "rotate-in-letter",
      duration: 0.8,
    },
    rollIn: {
      animationName: "rollIn",
      charClass: "roll-in-char",
      duration: 0.8,
    },
    flipOutX: {
      animationName: "flipOutX",
      charClass: "flip-out-char",
      duration: 0.8,
      ease: "ease-in",
    },
    flipOutY: {
      animationName: "flipOutY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-in",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceOut: {
      animationName: "bounceOut",
      charClass: "bounce-out-letter",
      duration: 0.9,
      ease: "ease-in",
    },
    rotateOut: {
      animationName: "rotateOut",
      charClass: "rotate-out-letter",
      duration: 0.8,
      ease: "ease-in",
    },
    rollOut: {
      animationName: "rollOut",
      charClass: "roll-out-char",
      duration: 0.7,
      ease: "ease-in",
    },
  };

  const [documentPlayData, setDocumentPlayData] = useState(documentPlayObj);
  const [nextPageToPlay, setNextPageToPlay] = useState({
    pageId: null,
    pageIdx: null,
    nextPageObj: null,
  });
  const [pagePlayData, setPagePlayData] = useState(pagePlayObj);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerVolumeRef = useRef();

  const playerHelper = {
    // prepare Data
    getDocumentData: () => {
      let pageArray = [];
      let documentDurationMs = 0;

      docPagesMeta?.pageNodes.forEach((pageObj, pageIdx) => {
        const blockObj = docPagesMeta?.blockNodes[pageIdx];
        const pageDurationMs = parseFloat(pageObj.pageDuration) * 1000;
        const pageStartTimeMs = documentDurationMs;
        const pageStopTimeMs = pageStartTimeMs + pageDurationMs;
        const prevPageIdx = docPagesMeta?.pageNodes?.[pageIdx - 1]?.pageIdx;
        const nextPageIdx = docPagesMeta?.pageNodes?.[pageIdx + 1]?.pageIdx;

        documentDurationMs += pageDurationMs;
        pageArray = [
          ...pageArray,
          {
            pageId: pageObj.pageId,
            pageIdx: pageIdx,
            blockId: blockObj.blockId,
            blockIdx: pageIdx,
            pageDurationMs,
            pageStartTimeMs,
            pageStopTimeMs,
            prevPageIdx: prevPageIdx ?? null,
            nextPageIdx: nextPageIdx ?? null,
          },
        ];
      });

      return {
        documentDurationMs,
        firstPageObj: pageArray[0],
        lastPageObj: pageArray[(docPagesMeta?.pageNodes?.length ?? 1) - 1],
        pageArray,
      };
    },

    getPageData: () => {
      let storage_data;
      if (!pagePlayData.pageId) {
        storage_data = operateStorageData({
          type: "session",
          action: "get",
          key: PAGE_PLAY_DATA_STORAGE_KEY,
          isParseable: true,
          defaultData: {},
        });
      }
      const pageId = pagePlayData.pageId ?? storage_data.pageId;
      const pageIdx = parseInt(pagePlayData.pageIdx ?? storage_data.pageIdx);
      const pageObj = docPagesMeta?.pageNodes[pageIdx];
      const pageDurationMs = parseFloat(pageObj?.pageDuration ?? 0) * 1000;

      return { pageId, pageIdx, pageObj, pageDurationMs };
    },

    getWidgetData: ({ widgetObj, pageDurationMs }) => {
      const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
      const startTimeMs = parseFloat(startTime) * 1000;
      const endTimeMs = parseFloat(endTime) * 1000;
      const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
      const widgetNode = document.querySelector(`#pagePlay #${widgetObj.id}`);
      const assetType = widgetObj.data["data-asset-type"];
      const source = widgetObj.data["data-source"];
      const frameContent = widgetObj.data["data-clipped"];

      return { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent };
    },

    getVideoNodeAndDataRef: ({ widgetId, widgetNode, videoNode }) => {
      const widget = widgetId ? document.querySelector(`#pagePlay #${widgetId}`) : widgetNode;
      const assetTypeCollage = widget.dataset.assetType === COLLAGE;
      const video = assetTypeCollage ? videoNode : widget?.querySelector("video");
      const { duration: durationInHMS, volume, trim, loop } = assetTypeCollage ? video?.dataset : widget?.dataset;
      const durationInSec = getHMSToSec({ hms: durationInHMS });
      const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
      const isTrimmed = Boolean(trimArray?.length === 2);
      const trimStart = isTrimmed ? trimArray?.[0] : 0;
      const trimEnd = isTrimmed ? trimArray?.[1] : durationInSec;

      return {
        widget,
        video,
        src: widget.dataset.assetType === FRAME ? video.querySelector("source")?.src : video.src,
        poster:
          widget.dataset.assetType === FRAME ? video.querySelector("source").getAttribute("data-poster") : video.poster,
        volume: volume ? parseInt(volume) : 100,
        durationInSec,
        durationInHMS,
        trimStart,
        trimEnd,
        isMuted: Boolean(video.getAttribute("muted")),
        isLooped: Boolean(loop === "true"),
        isTrimmed: Boolean(trimStart > 0 || trimEnd < durationInSec),
      };
    },

    // display only active page
    showSlides: slideNo => {
      const slides = document.getElementsByClassName("play-page");
      if (!slides?.length) return;

      // const total = slides.length;
      const prevSlideNo = slideNo > 0 ? slideNo - 1 : 0;

      // Hide only slides not needed
      [...slides].forEach((s, idx) => {
        if (idx !== prevSlideNo && idx !== slideNo) {
          s.classList.add("d-slide-none");
          s.classList.remove("page-playing");
          s.style.zIndex = "";
        }
      });

      // Show previous slide if slideNo > 0
      if (slideNo > 0) {
        slides[prevSlideNo]?.classList.remove("d-slide-none");
        slides[prevSlideNo].style.zIndex = "0";
      }

      // Show current slide
      slides[slideNo]?.classList.remove("d-slide-none");
      slides[slideNo]?.classList.add("page-playing");
      slides[slideNo].style.zIndex = "1";
    },

    // Handle Seek
    setPageTransitionCurTime: ({ pageObj, pageDurationMs, timer }) => {
      let wrapperNode;
      let animationNode;
      const pageNode = document.getElementById(`dhp-page-${pageObj.pageIdx}`);
      const blockNode = pageNode.getElementsByTagName("figure")[0];
      const showEntryAnimation = pageObj.pageTransition.enabled;
      const entryAnimationSpeed = pageObj.pageTransition.speed;
      const entryAnimationType = pageObj.pageTransition.type;
      const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTime = showEntryAnimation && entryAnimationDurationMs;
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

      if (!showEntryAnimation) return;

      if (timer > 0 && timer < pageDurationMs) {
        // when page is within playable time range
        // detecting wrapper/animation node
        if (!blockNode?.parentNode?.parentNode?.classList?.contains("wrapper-node")) {
          animationNode = document.createElement("div");
          animationNode.classList.add("animation-node");

          wrapperNode = document.createElement("div");
          wrapperNode.classList.add("wrapper-node");
          wrapperNode.style.overflow = "hidden";
          wrapperNode.style.height = "100%";
          wrapperNode.style.width = "100%";
          wrapperNode.appendChild(animationNode);

          blockNode.replaceWith(wrapperNode);
          wrapperNode.childNodes[0].appendChild(blockNode);
        } else {
          wrapperNode = pageNode.querySelector(".wrapper-node");
          animationNode = pageNode.querySelector(".animation-node");
        }

        // set page transition current time
        // page transition playable range
        if (showEntryAnimation && timer > 0 && timer < entryAnimationStopTime) {
          const currentTimerDurationMs = timer - 0;
          const animationPlayedDurationMs = parseInt(
            animationNode.getAttribute("data-page-transition-played-duration") ?? 0
          );

          animationNode.classList.add("play-animation");
          animationNode.classList.add(entryAnimationClassName);
          animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
          animationNode.style.animationPlayState = "paused";
          animationNode.style.animationDelay =
            currentTimerDurationMs > animationPlayedDurationMs
              ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
              : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;
        }
        // removing page transition props outside playable range
        else {
          animationNode.classList.remove("play-animation");
          animationNode.classList.remove(entryAnimationClassName);
          animationNode.style.animationDuration = "";
          animationNode.style.animationPlayState = "";
          animationNode.style.animationDelay = "";
          animationNode.removeAttribute("data-page-transition-played-duration");
        }
      } else {
        if (blockNode.parentNode.parentNode.classList.contains("wrapper-node")) {
          // when page is outside playable time range but page transition props were added on last preview
          wrapperNode = pageNode.querySelector(".wrapper-node");

          // removing wrapper/animation node (replacing with original node)
          wrapperNode.replaceWith(blockNode);
        }
      }
    },

    setDomPlayEnvToCustomTime: ({ pageId, pageObj, pageDurationMs, timer, meta }) => {
      const newPagePlayData = {
        pageId,
        pageIdx: meta.pageIdx,
        action: "pause",
      };

      setDocumentPlayData({
        ...documentPlayData,
        action: "pause",
        pageId,
        pageIdx: meta.pageIdx,
        documentProgress: meta.documentProgress,
        documentTimer: meta.documentTimer,
      });

      operateStorageData({
        type: "session",
        action: "set",
        key: DOCUMENT_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newPagePlayData,
      });
      setPagePlayData({
        ...pagePlayData,
        action: "pause",
        pageId,
        pageIdx: meta.pageIdx,
        pageTimer: meta.targetPageTimer,
        pageProgress: meta.pageProgress,
      });

      playerHelper.setPageTransitionCurTime({ pageObj, pageDurationMs, timer });

      docPagesMeta?.widgets.forEach(w => {
        if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
          const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = playerHelper.getWidgetData({
            widgetObj: w,
            pageDurationMs,
          });

          widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "" : "none";

          if (assetType) {
            playerHelper.setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
          }

          if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
            playerHelper.setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
          }

          if (assetType === COLLAGE) {
            widgetNode.querySelectorAll("video").forEach(videoNode => {
              playerHelper.setVideoCurTime({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
            });
          }

          if (assetType === FRAME && frameContent === "video") {
            playerHelper.setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
          }
        } else {
          const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = playerHelper.getWidgetData({
            widgetObj: w,
            pageDurationMs,
          });

          if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
            playerHelper.setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
          }

          if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
            playerHelper.setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
          }

          if (assetType === COLLAGE) {
            widgetNode.querySelectorAll("video").forEach(videoNode => {
              playerHelper.setVideoCurTime({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
            });
          }

          if (assetType === FRAME && frameContent === "video") {
            playerHelper.setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
          }
        }
      });

      playerHelper.setAudioCurTime({ timer: meta.documentTimer });
    },

    setVideoCurTime: ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
      const {
        video,
        trimStart: trimStartTime,
        trimEnd: trimStopTime,
      } = playerHelper.getVideoNodeAndDataRef({ widgetNode, videoNode });

      if (timer > startTimeMs && timer < stopTimeMs) {
        // when widget is within playable time range
        const widgetPlayedDurationMs = timer - startTimeMs;
        const videoTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
        const videoCurTimeWithinPlayableRange =
          (videoTrimmedDurationMs > widgetPlayedDurationMs
            ? widgetPlayedDurationMs
            : widgetPlayedDurationMs % videoTrimmedDurationMs) / 1000;

        video.currentTime = trimStartTime + videoCurTimeWithinPlayableRange;
      } else {
        video.currentTime = trimStartTime;
        // video.currentTime = startTimeMs;
      }
      video.pause();
    },

    setAudioCurTime: ({ timer }) => {
      docPagesMeta?.audios?.forEach(audioObj => {
        const { audio, startTimeMs, stopTimeMs, trimStartTimeMs, trimStopTimeMs } = playerHelper.getAudioData(audioObj);

        if (audio) {
          if (timer > startTimeMs && timer < stopTimeMs) {
            // when widget is within playable time range
            const widgetPlayedDurationMs = timer - startTimeMs;
            const audioTrimmedDurationMs = trimStopTimeMs - trimStartTimeMs;
            const audioCurTimeWithinPlayableRange =
              (audioTrimmedDurationMs > widgetPlayedDurationMs
                ? widgetPlayedDurationMs
                : widgetPlayedDurationMs % audioTrimmedDurationMs) / 1000;
            audio.currentTime = trimStartTimeMs / 1000 + audioCurTimeWithinPlayableRange;
          } else {
            audio.currentTime = trimStartTimeMs / 1000;
            // audio.currentTime = startTimeMs;
          }
          audio.pause();
        }
      });
    },

    setAnimationCurTime: ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
      const showEntryAnimation = widgetObj.data["data-entrance-animation"];
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTime = showEntryAnimation && startTimeMs + entryAnimationDurationMs;
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

      const showExitAnimation = widgetObj.data["data-exit-animation"];
      const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
      const exitAnimationType = widgetObj.data["data-exit-animation-type"];
      const exitAnimationDurationMs = showExitAnimation && animationSpeedToDurationMsObj[exitAnimationSpeed];
      const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
      const exitAnimationStartTime = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

      if (!showEntryAnimation && !showExitAnimation) return;

      // detecting animation node
      let animationNode = widgetNode.querySelector(".animation-node");
      let widgetChildNodes = animationNode?.childNodes ?? widgetNode.childNodes;

      if (timer > startTimeMs && timer < stopTimeMs) {
        // when widget is within playable time range
        if (!animationNode) {
          // create animation node if not exists (scenario: playing from beginning)
          animationNode = document.createElement("div");
          animationNode.classList.add("animation-node");
          animationNode.style.height = "100%";
          animationNode.style.width = "100%";
          [...widgetChildNodes].forEach(child => animationNode.append(child));
          widgetNode.innerHTML = "";
          widgetNode.append(animationNode);
        }

        // set animation current time
        // entry animation playable range
        if (showEntryAnimation && timer >= startTimeMs && timer < entryAnimationStopTime) {
          const currentTimerDurationMs = timer - startTimeMs;
          const animationPlayedDurationMs = parseInt(
            animationNode.getAttribute("data-entry-animation-played-duration") ?? 0
          );

          // adding entry animation props
          animationNode.classList.add("play-animation");
          animationNode.classList.add(entryAnimationClassName);
          animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
          animationNode.style.animationPlayState = "paused";
          animationNode.style.animationDelay =
            currentTimerDurationMs > animationPlayedDurationMs
              ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
              : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;

          // removing exit animation props
          animationNode.classList.remove(exitAnimationClassName);
          animationNode.removeAttribute("data-exit-animation-played-duration");
        }
        // exit animation playable range
        else if (showExitAnimation && timer >= exitAnimationStartTime && timer < stopTimeMs) {
          const currentTimerDurationMs = timer - exitAnimationStartTime;
          const animationPlayedDurationMs = parseInt(
            animationNode.getAttribute("data-exit-animation-played-duration") ?? 0
          );

          // adding exit animation props
          animationNode.classList.add("play-animation");
          animationNode.classList.add(exitAnimationClassName);
          animationNode.style.animationDuration = `${exitAnimationDurationMs}ms`;
          animationNode.style.animationPlayState = "paused";
          animationNode.style.animationDelay =
            currentTimerDurationMs > animationPlayedDurationMs
              ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
              : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;

          // removing entry animation props
          animationNode.classList.remove(entryAnimationClassName);
          animationNode.removeAttribute("data-entry-animation-played-duration");
        }
        // removing entry and exit animation props outside playable range
        else {
          animationNode.classList.remove("play-animation");
          animationNode.classList.remove(entryAnimationClassName);
          animationNode.classList.remove(exitAnimationClassName);
          animationNode.style.animationDuration = "";
          animationNode.style.animationPlayState = "";
          animationNode.style.animationDelay = "";
          animationNode.removeAttribute("data-entry-animation-played-duration");
          animationNode.removeAttribute("data-exit-animation-played-duration");
        }
      } else {
        if (animationNode) {
          // when widget is outside playable time range but animation props were added on last preview
          // re-assigning widgetChildNodes value as not working when force_stop
          widgetChildNodes = animationNode.childNodes;

          // replacing animation node with original node(s)
          widgetNode.innerHTML = "";
          [...widgetChildNodes].forEach(child => widgetNode.append(child));
        }
      }
    },

    handleSeek: e => {
      isSeekingRef.current = true;
      let documentProgress = e.target ? e.target.value : e;
      documentProgress = Math.min(documentProgress, 100);
      const { documentDurationMs, pageArray } = playerHelper.getDocumentData();
      const documentTimer = (documentProgress * documentDurationMs) / 100;
      const targetPageObj =
        pageArray.find(p => documentTimer >= p.pageStartTimeMs && documentTimer < p.pageStopTimeMs) ||
        pageArray[pageArray.length - 1];
      let targetPageTimer = documentTimer - targetPageObj.pageStartTimeMs;
      targetPageTimer = parseInt(targetPageTimer - (targetPageTimer % 50));
      const targetPageProgress = targetPageTimer * (100 / targetPageObj.pageDurationMs);
      let adjustedDocumentTimer = parseInt(documentTimer - (documentTimer % 50));
      let adjustedDocumentprogress = (adjustedDocumentTimer / documentDurationMs) * 100;

      // Step 1: Set the slide
      playerHelper.showSlides(targetPageObj.pageIdx);

      // Step 2: Wait ~50ms to ensure DOM renders before syncing environment
      setTimeout(() => {
        playerHelper.setDomPlayEnvToCustomTime({
          pageId: targetPageObj.pageId,
          pageObj: docPagesMeta.pageNodes[targetPageObj.pageIdx],
          pageDurationMs: targetPageObj.pageDurationMs,
          timer: targetPageTimer,
          meta: {
            pageIdx: targetPageObj.pageIdx,
            targetPageTimer,
            pageProgress: targetPageProgress,
            documentProgress: adjustedDocumentprogress,
            documentTimer: adjustedDocumentTimer,
          },
        });

        // Step 3: Update state
        setPagePlayData({
          ...pagePlayData,
          pageId: targetPageObj.pageId,
          pageIdx: targetPageObj.pageIdx,
          pageTimer: targetPageTimer,
          pageDurationMs: targetPageObj.pageDurationMs,
          action: "seek",
        });

        setDocumentPlayData(prev => ({
          ...prev,
          documentTimer: adjustedDocumentTimer,
          documentProgress: adjustedDocumentprogress,
        }));

        // Step 4: Release seek lock
        setTimeout(() => {
          isSeekingRef.current = false;
        }, 100);
      }, 50); // this allows DOM update/render before env sync
    },

    handleMouseDown: () => {
      if (isPlaying) {
        setPagePlayData({ ...pagePlayData, action: "pause" });
        setDocumentPlayData({ ...documentPlayData, action: "pause" });
      }
    },

    handleMouseUpOrLeave: () => {
      setTimeout(() => {
        if (isPlaying && !isSeekingRef.current) {
          const idValue = document.querySelector(".page-playing")?.getAttribute("id");
          const curPageIdx = parseInt(idValue?.match(/\d+/)?.[0]);
          const curPageId = docPagesMeta?.pageNodes[curPageIdx]?.pageId;

          setPagePlayData({ ...pagePlayData, action: "play" });
          setDocumentPlayData({ ...documentPlayData, action: "play" });

          operateStorageData({
            type: "session",
            action: "set",
            key: DOCUMENT_PLAY_DATA_STORAGE_KEY,
            isParseable: true,
            defaultData: {
              pageId: curPageId,
              pageIdx: curPageIdx,
              action: "play",
            },
          });
        }
      }, 100); // ensure lock is released before resuming
    },

    // Play page/widgets
    pagePlay: ({ timer, pageDurationMs, pageObj, pageId, pageIdx }) => {
      const pageNode = document.getElementById(`dhp-page-${pageIdx}`);
      const progress = customRound((100 / pageDurationMs) * timer, 2);
      const storage_data = operateStorageData({
        type: "session",
        action: "get",
        key: PAGE_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: {
          action: "play",
        },
      });

      setPagePlayData({
        action: timer === pageDurationMs ? "natural_stop" : storage_data.action,
        pageId: pageId ?? storage_data.pageId,
        pageIdx: pageIdx ?? storage_data.pageIdx,
        pageTimer: timer,
        pageProgress: progress,
        pageDurationMs: pageDurationMs,
      });

      if (timer === 0) {
        // page play start
        pageNode.classList.add("page-playing");
        playerHelper.pageTransitionPlay({ timer, pageDurationMs, pageObj });
      }

      if (timer === pageDurationMs || parseInt(progress) >= 100) {
        // page play stop
        pageNode.classList.remove("page-playing");
        playerHelper.pageTransitionPlay({ timer, pageDurationMs, pageObj });
      }
    },

    pageTransitionPlay: ({ pageObj, pageDurationMs, timer }) => {
      const pageIdx = pageObj.pageIdx;
      const pageNode = document.querySelector(`#pagePlay #dhp-page-${pageIdx}`);
      if (!pageNode) return;

      const figure = pageNode.getElementsByTagName("figure")[0];
      if (!figure) return;

      const enabled = !!(pageObj.pageTransition && pageObj.pageTransition.enabled);
      if (!enabled) return;

      const speedKey = pageObj.pageTransition.speed;
      const animType = pageObj.pageTransition.type;
      const durationMs = animationSpeedToDurationMsObj[speedKey];
      const animClass = stringTocamelCase(animType);

      const target = figure.classList.contains("animation-target")
        ? figure
        : figure.querySelector(".animation-target") || figure;

      target.style.animationFillMode = "both";
      target.style.animationDuration = `${durationMs}ms`;
      target.style.willChange = "transform, opacity";
      target.style.backfaceVisibility = "hidden";

      const startAnimation = seekMs => {
        if (target.__animHandler) {
          target.removeEventListener("animationend", target.__animHandler);
          target.__animHandler = null;
        }

        const onEnd = () => {
          target.classList.remove("play-animation", animClass);
          target.style.animationDelay = "";
          target.style.animationPlayState = "";
        };
        target.__animHandler = onEnd;
        target.addEventListener("animationend", onEnd, { once: true });

        requestAnimationFrame(() => {
          void target.offsetWidth;
          target.classList.add("play-animation", animClass);

          if (typeof seekMs === "number" && seekMs > 0) {
            target.style.animationDelay = `-${seekMs}ms`;
            target.style.animationPlayState = "running";
          } else {
            target.style.animationDelay = "";
            target.style.animationPlayState = "";
          }
        });
      };

      if (timer === 0) {
        startAnimation(0);
      } else if (timer > 0 && timer < durationMs) {
        startAnimation(timer);
      } else if (timer === durationMs) {
        target.classList.remove("play-animation", animClass);
        target.style.animationDelay = "";
        target.style.animationPlayState = "";
      }

      if (timer === pageDurationMs) {
        target.classList.remove("play-animation", animClass);
        target.style.animationDelay = "";
        target.style.animationPlayState = "";
      }
    },

    widgetPlay: ({ timer, pageDurationMs, pageId }) => {
      // widget play-pause
      docPagesMeta?.widgets?.forEach(w => {
        if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
          const widgetNode = document.querySelector(`#pagePlay #${w.id}`);
          const { startTimeMs, stopTimeMs, assetType, source, frameContent } = playerHelper.getWidgetData({
            widgetObj: w,
            pageDurationMs,
          });

          if (timer === startTimeMs) {
            widgetNode.style.display = "";
          }

          if (assetType) {
            playerHelper.animationPlay({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
          }

          if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
            playerHelper.videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
          }

          if (assetType === COLLAGE) {
            widgetNode.querySelectorAll("video").forEach(videoNode => {
              playerHelper.videoPlay({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
            });
          }

          if (assetType === FRAME && frameContent === "video") {
            playerHelper.videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
          }

          if (timer === stopTimeMs && stopTimeMs < pageDurationMs) {
            widgetNode.style.display = "none";
          }

          // if (timer === pageDurationMs) {
          //   // page duration end
          //   widgetNode.style.display = "block";
          // }
        }
      });
    },

    videoPlay: ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
      const {
        video,
        volume,
        trimStart: trimStartTime,
        trimEnd: trimStopTime,
        isLooped,
      } = playerHelper.getVideoNodeAndDataRef({ widgetNode, videoNode });
      const videoCurTime = floatToFloatSec(video.currentTime);

      if (timer === startTimeMs) {
        if (!(videoCurTime >= trimStartTime && videoCurTime <= trimStopTime)) video.currentTime = trimStartTime;
        video.muted = false;
        video.volume = ((volume / 100) * playerVolumeRef.current) / 100;
        video.controls = false;
        playMedia(video);
        video.setAttribute("data-playing", "true");
      }

      if (timer > startTimeMs && timer < stopTimeMs) {
        if (!(videoCurTime >= trimStartTime && videoCurTime <= trimStopTime)) {
          // when video is outside playable time range
          video.currentTime = trimStartTime;
          video.muted = false;
          video.volume = ((volume / 100) * playerVolumeRef.current) / 100;
          if (isLooped) {
            if (video.paused) playMedia(video);
          } else {
            video.pause();
          }
        } else {
          video.muted = false;
          video.volume = ((volume / 100) * playerVolumeRef.current) / 100;
          if (video.paused) playMedia(video);
        }
      }

      if (timer === stopTimeMs) {
        video.pause();
        video.muted = true;
        video.removeAttribute("data-playing");
        // video.currentTime = trimStartTime;
        // video.load();
      }
    },

    animationPlay: ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
      const showEntryAnimation = widgetObj.data["data-entrance-animation"];
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTime = showEntryAnimation && startTimeMs + entryAnimationDurationMs;
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

      const showExitAnimation = widgetObj.data["data-exit-animation"];
      const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
      const exitAnimationType = widgetObj.data["data-exit-animation-type"];
      const exitAnimationDurationMs = showExitAnimation && animationSpeedToDurationMsObj[exitAnimationSpeed];
      const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
      const exitAnimationStartTime = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

      if (!showEntryAnimation && !showExitAnimation) return;

      // detecting animation node
      let animationNode = widgetNode.querySelector(".animation-node");
      let widgetChildNodes = animationNode?.childNodes ?? widgetNode.childNodes;

      if (!animationNode) {
        // create animation node if not exists (scenario: playing from beginning)
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");
        animationNode.style.height = "100%";
        animationNode.style.width = "100%";
        [...widgetChildNodes].forEach(child => animationNode.append(child));
        widgetNode.innerHTML = "";
        widgetNode.append(animationNode);
      }

      // play / pause animations
      // -------------- entry animation start ---------------------//
      if (showEntryAnimation && timer === startTimeMs) {
        const preset = TEXT_ANIMATION_PRESETS[entryAnimationClassName];

        // text charater animation
        if (preset) {
          const el = widgetNode.querySelector(".primary-text-widget");
          if (!el) return;

          // store original html
          const originalHTML = el.innerHTML;
          widgetNode.setAttribute("data-original-html", originalHTML);

          // ✅ apply beforeWrap if exists
          const context = preset.beforeWrap ? preset.beforeWrap(el) : el;

          // ✅ wrap text nodes
          const wrapTextNodes = node => {
            [...node.childNodes].forEach(child => {
              if (child.nodeType === 3 && child.textContent.trim().length > 0) {
                const fragment = document.createDocumentFragment();

                const words = child.textContent.split(" ");

                words.forEach((word, wordIndex) => {
                  const wordSpan = document.createElement("span");
                  wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
                  wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

                  [...word].forEach(char => {
                    const charSpan = document.createElement("span");
                    charSpan.className = preset.charClass;
                    charSpan.innerHTML = char === " " ? "&nbsp;" : char;
                    wordSpan.appendChild(charSpan);
                    // animatedCount++;
                  });

                  fragment.appendChild(wordSpan);

                  // add space after word (except last)
                  if (wordIndex !== words.length - 1) {
                    fragment.appendChild(document.createTextNode(" "));
                  }
                });

                child.replaceWith(fragment);
              } else if (child.nodeType === 1) {
                wrapTextNodes(child);
              }
            });
          };

          wrapTextNodes(context);

          const chars = context.querySelectorAll(`.${preset.charClass}`);
          const charCount = chars.length;
          if (!charCount) return;

          // ✅ SAME logic as your main engine
          const maxTotalTime = entryAnimationDurationMs / 1000;
          const animDuration = Math.min(preset.duration, maxTotalTime);

          const perCharDelay = charCount > 1 ? Math.max((maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

          // ✅ apply animation properly
          chars.forEach((span, i) => {
            span.classList.add("play-animation"); // IMPORTANT

            span.style.animation = `
        ${preset.animationName}
        ${animDuration}s
        ${i * perCharDelay}s
        both
        ${preset.ease || "ease-out"}
      `;
          });
        }
        // other widget animation
        else {
          animationNode.classList.add("play-animation");
          animationNode.classList.add(entryAnimationClassName);
          animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
        }
      }

      if (showEntryAnimation && timer > startTimeMs && timer < entryAnimationStopTime) {
        animationNode.setAttribute("data-entry-animation-played-duration", timer);
      }
      // -------------- entry animation stop ---------------------//
      if (showEntryAnimation && timer === entryAnimationStopTime) {
        const preset = TEXT_ANIMATION_PRESETS[entryAnimationClassName];

        // text charater animation
        if (preset) {
          const el = widgetNode.querySelector(".primary-text-widget");
          const originalHTML = widgetNode.getAttribute("data-original-html");

          if (el && originalHTML) {
            el.innerHTML = originalHTML;

            // ✅ restore wrapper (flipY case)
            if (preset.afterRestore) {
              preset.afterRestore(el);
            }

            widgetNode.removeAttribute("data-original-html");
          }
        }
        // other widget animation
        else {
          animationNode.classList.remove("play-animation");
          animationNode.classList.remove(entryAnimationClassName);
          animationNode.style.animationDuration = "";
          animationNode.style.animationPlayState = "";
          animationNode.style.animationDelay = "";
          animationNode.removeAttribute("data-entry-animation-played-duration");
        }
      }

      //---------------- exit animation start ------------------//
      if (showExitAnimation && timer === exitAnimationStartTime) {
        const preset = TEXT_ANIMATION_PRESETS[exitAnimationClassName];

        // TEXT character EXIT ANIMATION
        if (preset) {
          const el = widgetNode.querySelector(".primary-text-widget");
          if (!el) return;

          // store original HTML
          const originalHTML = el.innerHTML;
          widgetNode.setAttribute("data-exit-original-html", originalHTML);

          // beforeWrap (for flipY etc.)
          const context = preset.beforeWrap ? preset.beforeWrap(el) : el;

          // wrap text nodes
          const wrapTextNodes = node => {
            [...node.childNodes].forEach(child => {
              if (child.nodeType === 3 && child.textContent.trim().length > 0) {
                const fragment = document.createDocumentFragment();

                const words = child.textContent.split(" ");

                words.forEach((word, wordIndex) => {
                  const wordSpan = document.createElement("span");
                  wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
                  wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

                  [...word].forEach(char => {
                    const charSpan = document.createElement("span");
                    charSpan.className = preset.charClass;
                    charSpan.innerHTML = char === " " ? "&nbsp;" : char;
                    wordSpan.appendChild(charSpan);
                    // animatedCount++;
                  });

                  fragment.appendChild(wordSpan);

                  // add space after word (except last)
                  if (wordIndex !== words.length - 1) {
                    fragment.appendChild(document.createTextNode(" "));
                  }
                });

                child.replaceWith(fragment);
              } else if (child.nodeType === 1) {
                wrapTextNodes(child);
              }
            });
          };

          wrapTextNodes(context);

          const chars = context.querySelectorAll(`.${preset.charClass}`);
          const charCount = chars.length;
          if (!charCount) return;

          // timing
          const maxTotalTime = exitAnimationDurationMs / 1000;
          const animDuration = Math.min(preset.duration, maxTotalTime);

          const perCharDelay = charCount > 1 ? Math.max((maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

          // apply animation
          chars.forEach((span, i) => {
            span.classList.add("play-animation");

            span.style.animation = `
        ${preset.animationName}
        ${animDuration}s
        ${i * perCharDelay}s
        both
        ${preset.ease || "ease-in"}
      `;
          });
        }
        // NORMAL EXIT ANIMATION
        else {
          animationNode.classList.add("play-animation");
          animationNode.classList.add(exitAnimationClassName);
          animationNode.style.animationDuration = `${exitAnimationDurationMs}ms`;
        }
      }

      if (showExitAnimation && timer > exitAnimationStartTime && timer < stopTimeMs) {
        animationNode.setAttribute("data-exit-animation-played-duration", timer - exitAnimationStartTime);
      }
      //------------------ exit animation stop ------------------//
      if (showExitAnimation && timer === stopTimeMs) {
        const preset = TEXT_ANIMATION_PRESETS[exitAnimationClassName];

        // TEXT character restore
        if (preset) {
          const el = widgetNode.querySelector(".primary-text-widget");
          const originalHTML = widgetNode.getAttribute("data-exit-original-html");

          if (el && originalHTML) {
            el.innerHTML = originalHTML;

            if (preset.afterRestore) {
              preset.afterRestore(el);
            }

            widgetNode.removeAttribute("data-exit-original-html");
          }
        }
        // NORMAL restore
        else {
          animationNode.classList.remove("play-animation");
          animationNode.classList.remove(exitAnimationClassName);
          animationNode.style.animationDuration = "";
          animationNode.style.animationPlayState = "";
          animationNode.style.animationDelay = "";
          animationNode.removeAttribute("data-exit-animation-played-duration");
        }
      }

      if (timer === stopTimeMs) {
        // widget/page duration end
        // re-assigning widgetChildNodes value as not working when force_stop
        widgetChildNodes = animationNode.childNodes;

        // replacing animation node with original node(s)
        widgetNode.innerHTML = "";
        if (showExitAnimation) widgetNode.style.display = "none";
        [...widgetChildNodes].forEach(child => widgetNode.append(child));
      }
    },

    // pause/stop
    stopDomPlay: () => {
      if (intervalId?.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      document.querySelectorAll(".play-animation").forEach(animationNode => {
        animationNode.style.animationPlayState = "paused";
      });
      document.querySelectorAll('video[data-playing="true"]').forEach(videoNode => {
        videoNode.pause();
      });
    },

    stopAudioPlay: () => {
      if (audioIntervalId?.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }
      globalAudioTimer.current = 0;

      document.querySelectorAll('audio[data-playing="true"]').forEach(audioNode => {
        audioNode.pause();
        audioNode.removeAttribute("data-playing");
      });
    },

    pauseDocumentPlay: () => {
      if (intervalId?.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      if (audioIntervalId?.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }
    },

    stopDocumentPlay: () => {
      const { lastPageObj, documentDurationMs } = playerHelper.getDocumentData();
      // playerHelper.showSlides(firstPageObj.pageIdx);
      setPagePlayData({
        ...pagePlayData,
        pageId: lastPageObj.pageId,
        pageIdx: lastPageObj.pageIdx,
        pageTimer: lastPageObj.pageStopTimeMs,
        pageProgress: 100,
      });
      setDocumentPlayData({
        action: null,
        pageId: lastPageObj.pageId,
        pageIdx: lastPageObj.pageIdx,
        documentTimer: documentDurationMs,
        documentProgress: 100,
        documentDurationMs: documentDurationMs,
        pageArray: [],
      });
      setNextPageToPlay({
        pageId: null,
        pageIdx: null,
        nextPageObj: null,
      });
      setIsPlaying(false);
      // stop audio
      if (audioIntervalId?.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }
      docPagesMeta?.audios?.forEach(audioObj => {
        const { trimStartTimeMs } = playerHelper.getAudioData(audioObj);
        const audio = document.getElementById(`player-${audioObj.id}`);
        if (audio) {
          audio.pause();
          audio.currentTime = trimStartTimeMs / 1000;
          audio.removeAttribute("data-playing");
          audio.load();
        }
      });
      document.querySelectorAll("#playPage video").forEach(videoNode => {
        videoNode.pause();
        // videoNode.load();
      });

      // playerHelper.setDomPlayEnvToCustomTime({
      //   pageId: firstPageObj.pageId,
      //   pageObj: docPagesMeta.pageNodes[0],
      //   pageDurationMs: firstPageObj.pageDurationMs,
      //   timer: 0,
      //   meta: {
      //     pageIdx: firstPageObj.pageIdx,
      //     targetPageTimer: 0,
      //     pageProgress: 0,
      //     documentProgress: 0,
      //     documentTimer: 0,
      //   },
      // });
    },
    resumeDomPlayEnv: ({ timer, pageDurationMs, pageId }) => {
      // setting environment for resuming page play where left off (widget show/hide)
      docPagesMeta?.widgets.forEach(w => {
        if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
          const { startTimeMs, stopTimeMs, widgetNode } = playerHelper.getWidgetData({ widgetObj: w, pageDurationMs });
          widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "" : "none";
        }
      });

      // setting environment for resuming page play where left off (page transition + widget animation)
      document.querySelectorAll(".play-animation").forEach(element => {
        element.style.animationPlayState = "";
      });
    },

    togglePlayPause: isPlaying => {
      const willPlay = !isPlaying;
      // flip UI state
      setIsPlaying(willPlay);

      // default page/document play action
      let newDocumentPlayData = { action: willPlay ? "play" : "pause" };

      // If user is trying to play but document is already at 100%, reset to start
      const { firstPageObj, documentDurationMs } = playerHelper.getDocumentData();
      const atDocumentEnd =
        documentPlayData &&
        (documentPlayData.documentProgress >= 100 ||
          (documentPlayData.documentTimer != null && documentPlayData.documentTimer >= documentDurationMs));

      if (willPlay && atDocumentEnd) {
        // reset document + page timers to beginning so play actually starts from 0
        newDocumentPlayData = {
          action: "play",
          documentTimer: 0,
          documentProgress: 0,
          pageId: firstPageObj.pageId,
          pageIdx: firstPageObj.pageIdx,
        };

        // reset page state immediately so UI reflects start
        setPagePlayData(prev => ({
          ...prev,
          action: "play",
          pageId: firstPageObj.pageId,
          pageIdx: firstPageObj.pageIdx,
          pageTimer: 0,
          pageProgress: 0,
          pageDurationMs: firstPageObj.pageDurationMs,
        }));

        // show first slide and reset DOM to time 0
        playerHelper.showSlides(firstPageObj.pageIdx);
        playerHelper.setDomPlayEnvToCustomTime({
          pageId: firstPageObj.pageId,
          pageObj: docPagesMeta.pageNodes[firstPageObj.pageIdx],
          pageDurationMs: firstPageObj.pageDurationMs,
          timer: 0,
          meta: {
            pageIdx: firstPageObj.pageIdx,
            targetPageTimer: 0,
            pageProgress: 0,
            documentProgress: 0,
            documentTimer: 0,
          },
        });
      }

      setDocumentPlayData(prev => ({ ...prev, ...newDocumentPlayData }));
      operateStorageData({
        type: "session",
        action: "set",
        key: DOCUMENT_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newDocumentPlayData,
      });
      operateStorageData({
        type: "session",
        action: "set",
        key: PAGE_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newDocumentPlayData,
      });
    },

    // play Audio
    getAudioData: audioObj => {
      let fadeData = {};
      const audio = document.getElementById(`player-${audioObj.id}`);
      const durationInHMS = audioObj.data["data-duration"];
      const durationInSec = getHMSToSec({ hms: durationInHMS });

      const fadeInValue = audioObj.data["data-fade-in-value"];
      const fadeOutValue = audioObj.data["data-fade-out-value"];
      const fadeInMs = parseInt(Object.keys(FADE_EFFECT_OBJ).find(k => FADE_EFFECT_OBJ[k].includes(fadeInValue)));
      const fadeOutMs = parseInt(Object.keys(FADE_EFFECT_OBJ).find(k => FADE_EFFECT_OBJ[k].includes(fadeOutValue)));

      const trim = audioObj.data["data-trim"];
      const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
      const isTrimmed = Boolean(trimArray?.length === 2);
      const trimStart = isTrimmed ? trimArray?.[0] : 0;
      const trimStop = isTrimmed ? trimArray?.[1] : durationInSec;

      const volume = parseInt(audioObj.data["data-volume"] ?? 100);
      const startTimeMs = parseFloat(audioObj.startTime) * 1000;
      const stopTimeMs = parseFloat(audioObj.endTime) * 1000;
      const trimStartTimeMs = trimStart * 1000;
      const trimStopTimeMs = trimStop * 1000;
      const trimmedDurationMs = trimStopTimeMs - trimStartTimeMs;

      const fadeInStartTimeMs = startTimeMs;
      const fadeInStopTimeMs = startTimeMs + fadeInMs;
      const fadeOutStartTimeMs = stopTimeMs - fadeOutMs;
      const fadeOutStopTimeMs = stopTimeMs;

      if (fadeInMs > 0 || fadeOutMs > 0) {
        fadeData = playerHelper.getFadeData({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs });
      }

      return {
        audio,
        audioCurTimeMs: floatToFloatSec(audio.currentTime) * 1000,
        volume: volume ? parseInt(volume) : 100,
        startTimeMs,
        stopTimeMs,
        trimStartTimeMs,
        trimStopTimeMs,
        trimmedDurationMs,
        fadeInStartTimeMs,
        fadeInStopTimeMs,
        fadeOutStartTimeMs,
        fadeOutStopTimeMs,
        ...fadeData,
        isMuted: Boolean(volume === 0),
        isLooped: Boolean(stopTimeMs > trimStopTimeMs),
        isTrimmed: Boolean(trimStartTimeMs > 0 || trimStopTimeMs < stopTimeMs),
        isFadeIn: Boolean(fadeInMs > 0),
        isFadeOut: Boolean(fadeOutMs > 0),
      };
    },

    getFadeData: ({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs }) => {
      let fadeInIntervals = [];
      let fadeInVolumes = [];
      let fadeOutIntervals = [];
      let fadeOutVolumes = [];

      if (fadeInMs > 0) {
        const intervals = FADE_INTERVEL_OBJ[fadeInMs];
        const volumeFactor = volume / (intervals.length - 1);

        for (let i = 0; i < intervals.length; i++) {
          fadeInIntervals = [...fadeInIntervals, fadeInStartTimeMs + intervals[i]];

          if (i === 0) fadeInVolumes = [...fadeInVolumes, 0];
          if (i > 0 && i < intervals.length - 1) fadeInVolumes = [...fadeInVolumes, parseInt(i * volumeFactor)];
          if (i === intervals.length - 1) fadeInVolumes = [...fadeInVolumes, volume];
        }
      }

      if (fadeOutMs > 0) {
        const intervalObj = JSON.parse(JSON.stringify(FADE_INTERVEL_OBJ[fadeOutMs]));
        const intervals = intervalObj.reverse();
        const volumeFactor = volume / (intervals.length - 1);

        for (let i = intervals.length - 1; i >= 0; i--) {
          fadeOutIntervals = [...fadeOutIntervals, fadeOutStartTimeMs + intervals[i]];

          if (i === intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, volume];
          if (i > 0 && i < intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, parseInt(i * volumeFactor)];
          if (i === 0) fadeOutVolumes = [...fadeOutVolumes, 0];
        }
      }

      return { fadeInIntervals, fadeInVolumes, fadeOutIntervals, fadeOutVolumes };
    },

    getPageStartTimes: () => {
      let pageStartTimes = [];
      let documentDurationMs = 0;

      docPagesMeta?.pageNodes.forEach(pageObj => {
        const pageDurationMs = parseFloat(pageObj.pageDuration) * 1000;
        const pageStartTimeMs = documentDurationMs;

        documentDurationMs += pageDurationMs;
        pageStartTimes = [...pageStartTimes, pageStartTimeMs];
      });

      return pageStartTimes;
    },

    audioPlay: ({ timer }) => {
      let playingPageIdx = document.querySelector(".play-page.page-playing")?.getAttribute("id") ?? "dhp-page-0";
      playingPageIdx = playingPageIdx?.match(/\d+$/)?.[0];
      if (!playingPageIdx || !playerVolumeRef.current > 0) return;

      playingPageIdx = parseInt(playingPageIdx);
      const pageStart = playerHelper.getactvePageStartTime({ pageIdx: playingPageIdx });

      // timer is relative to reduced timeline → shift into document timeline
      const docTimer = pageStart + timer;

      docPagesMeta?.audios?.forEach(audioObj => {
        const {
          audio,
          audioCurTimeMs,
          volume,
          startTimeMs,
          stopTimeMs,
          trimStartTimeMs,
          trimStopTimeMs,
          fadeInIntervals,
          fadeInVolumes,
          fadeOutIntervals,
          fadeOutVolumes,
          isMuted,
          isLooped,
          isFadeIn,
          isFadeOut,
        } = playerHelper.getAudioData(audioObj);

        if (!audio) return;

        const isInFadeRange = arr =>
          Array.isArray(arr) && arr.length > 0 && docTimer >= Math.min(...arr) && docTimer <= Math.max(...arr);

        const inFadeIn = isFadeIn && isInFadeRange(fadeInIntervals);
        const inFadeOut = isFadeOut && isInFadeRange(fadeOutIntervals);
        const fadeIsActive = inFadeIn || inFadeOut;

        // Start playback
        if (docTimer === startTimeMs) {
          if (isMuted) audio.muted = false;
          if (!inFadeIn) {
            audio.volume = ((volume / 100) * playerVolumeRef.current) / 100;
          }
          audio.currentTime = trimStartTimeMs / 1000;
          playMedia(audio);
          audio.setAttribute("data-playing", "true");
        }

        // Fade In
        if (inFadeIn && fadeInIntervals.includes(docTimer)) {
          const idx = fadeInIntervals.indexOf(docTimer);
          audio.volume = ((fadeInVolumes[idx] / 100) * playerVolumeRef.current) / 100;
        }

        // Fade Out
        if (inFadeOut && fadeOutIntervals.includes(docTimer)) {
          const idx = fadeOutIntervals.indexOf(docTimer);
          audio.volume = ((fadeOutVolumes[idx] / 100) * playerVolumeRef.current) / 100;
        }

        // While playing
        if (docTimer > startTimeMs && docTimer < stopTimeMs) {
          if (!fadeIsActive) {
            audio.volume = ((volume / 100) * playerVolumeRef.current) / 100;
          }
          if (!(audioCurTimeMs >= trimStartTimeMs && audioCurTimeMs <= trimStopTimeMs)) {
            audio.currentTime = (docTimer - startTimeMs + trimStartTimeMs) / 1000;

            if (isLooped) {
              if (audio.paused) {
                playMedia(audio);
                audio.setAttribute("data-playing", "true");
              }
            } else {
              audio.pause();
            }
          } else {
            if (docTimer - startTimeMs + trimStartTimeMs - audioCurTimeMs >= 1000) {
              audio.currentTime = (docTimer - startTimeMs + trimStartTimeMs) / 1000;
            }
            if (audio.paused) {
              playMedia(audio);
              audio.setAttribute("data-playing", "true");
            }
          }
        }

        // Stop playback
        if (docTimer >= stopTimeMs) {
          audio.pause();
          if (isMuted) audio.muted = true;
          audio.removeAttribute("data-playing");
          audio.currentTime = trimStartTimeMs / 1000;
          audio.load();
        }
      });
    },

    getactvePageStartTime: ({ pageIdx }) => {
      if (!docPagesMeta?.pageNodes || !docPagesMeta?.allPageNodes) return 0;

      // 1. Get current pageId from pageNodes
      const curPageId = docPagesMeta.pageNodes.find(p => p.pageIdx === pageIdx)?.pageId;
      if (!curPageId) return 0;

      // 2. Find its index in allPageNodes
      const curAllIdx = docPagesMeta.allPageNodes.findIndex(p => p.pageId === curPageId);
      if (curAllIdx < 0) return 0;

      // 3. Timeline start time in allPageNodes
      let allTimelineSec = 0;
      for (let i = 0; i < curAllIdx; i++) {
        allTimelineSec += parseFloat(docPagesMeta.allPageNodes[i].pageDuration || 0);
      }

      // 4. Timeline start time in active pageNodes
      const activeIndices = (docPagesMeta.pageNodes || [])
        .map(p => p.pageIdx)
        .filter(x => typeof x === "number")
        .sort((a, b) => a - b);

      const pos = activeIndices.indexOf(pageIdx);
      let activeTimelineSec = 0;
      for (let i = 0; i < pos; i++) {
        const idx = activeIndices[i];
        const pn = docPagesMeta.allPageNodes.find(p => p.pageIdx === idx);
        if (pn) activeTimelineSec += parseFloat(pn.pageDuration || 0);
      }

      // 5. Delta between actual timeline vs reduced (active) timeline
      const deltaSec = allTimelineSec - activeTimelineSec;

      return deltaSec * 1000; // ms
    },

    controllVolume: vol => {
      playerVolumeRef.current = parseFloat(vol) * 100;
    },

    // preloadMedia: () => {
    //   // Preload all videos
    //   document.querySelectorAll("video").forEach(video => {
    //     if (video.readyState < 1) {
    //       video.load();
    //     }
    //   });

    //   // Preload all audios
    //   document.querySelectorAll("audio").forEach(audio => {
    //     if (audio.readyState < 1) {
    //       audio.load();
    //     }
    //   });
    // },
  };

  useEffect(() => {
    if (documentPlayData.action === "play") {
      if (!documentPlayData.documentTimer) {
        // start document/page play from begining
        const { firstPageObj } = playerHelper.getDocumentData();
        const newPagePlayData = {
          action: "play",
          pageId: firstPageObj.pageId,
          pageIdx: firstPageObj.pageIdx,
        };
        setPagePlayData({
          ...pagePlayData,
          ...newPagePlayData,
        });
        operateStorageData({
          type: "session",
          action: "set",
          key: PAGE_PLAY_DATA_STORAGE_KEY,
          isParseable: true,
          defaultData: newPagePlayData,
        });
      } else {
        // resume document/page play where it left off
        const newPagePlayData = {
          action: "play",
          pageId: documentPlayData.pageId,
          pageIdx: documentPlayData.pageIdx,
        };
        setPagePlayData({
          ...pagePlayData,
          ...newPagePlayData,
        });
        operateStorageData({
          type: "session",
          action: "set",
          key: PAGE_PLAY_DATA_STORAGE_KEY,
          isParseable: true,
          defaultData: newPagePlayData,
        });
      }

      if (audioIntervalId.current) clearInterval(audioIntervalId.current);

      if (!audioIntervalId?.current) {
        globalAudioTimer.current = documentPlayData?.documentTimer ?? 0;

        if (globalAudioTimer.current === 0) {
          playerHelper.audioPlay({ timer: globalAudioTimer.current });
        }

        audioIntervalId.current = setInterval(() => {
          globalAudioTimer.current += 50;
          playerHelper.audioPlay({ timer: globalAudioTimer.current });
        }, 50);
      }
    }

    if (documentPlayData.action === "pause") {
      const newPagePlayData = {
        action: "pause",
        pageId: documentPlayData.pageId,
        pageIdx: documentPlayData.pageIdx,
      };
      playerHelper.stopAudioPlay();
      setPagePlayData({
        ...pagePlayData,
        ...newPagePlayData,
      });
      operateStorageData({
        type: "session",
        action: "set",
        key: PAGE_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newPagePlayData,
      });
    }

    if (documentPlayData.action === "natural_stop") {
      playerHelper.stopDocumentPlay();
    }

    return () => {
      playerHelper.pauseDocumentPlay();
    };
  }, [documentPlayData.action]);

  useEffect(() => {
    if (nextPageToPlay.pageId) {
      const { pageId, pageIdx } = nextPageToPlay;
      const newPagePlayData = { action: "play", pageId, pageIdx };

      // set page play instructions
      setPagePlayData({
        ...pagePlayData,
        ...newPagePlayData,
      });
      operateStorageData({
        type: "session",
        action: "set",
        key: PAGE_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newPagePlayData,
      });

      setDocumentPlayData({
        ...documentPlayData,
        ...newPagePlayData,
      });
      operateStorageData({
        type: "session",
        action: "set",
        key: DOCUMENT_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: newPagePlayData,
      });
    }
  }, [nextPageToPlay]);

  useEffect(() => {
    if (isSeekingRef.current) return; // prevent slide update during seek
    if (documentPlayData.action && pagePlayData.pageTimer) {
      const { documentDurationMs, pageArray } = playerHelper.getDocumentData();
      const prevPageIdx = pageArray?.[pagePlayData.pageIdx]?.prevPageIdx;
      const nextPageIdx = pageArray?.[pagePlayData.pageIdx]?.nextPageIdx;
      const nextPageObj = pageArray?.[nextPageIdx];

      if (pagePlayData.pageTimer > 0 && pagePlayData.pageTimer <= pagePlayData.pageDurationMs) {
        // update document timer and progress on each page play interval of 100ms
        let timer = 0;
        let progress = 0;

        pageArray.forEach((pageObj, idx) => {
          if (prevPageIdx >= 0 && idx < pagePlayData.pageIdx) timer += pageObj.pageDurationMs;
        });

        timer += pagePlayData.pageTimer;
        progress = customRound((100 / documentDurationMs) * timer, 2);
        setDocumentPlayData({
          ...documentPlayData,
          pageId: pagePlayData.pageId,
          pageIdx: pagePlayData.pageIdx,
          documentTimer: timer,
          documentProgress: progress,
          documentDurationMs,
          pageArray,
        });
      }

      if (pagePlayData.pageTimer >= pagePlayData.pageDurationMs) {
        // at end of each page duration
        if (nextPageIdx !== undefined && nextPageIdx !== null) {
          playerHelper.showSlides(nextPageIdx);
          // requestAnimationFrame(() => {
          setNextPageToPlay({
            pageId: nextPageObj.pageId,
            pageIdx: nextPageObj.pageIdx,
            nextPageObj,
          });
          // });
        } else {
          // natural end of document play
          setDocumentPlayData({
            ...documentPlayData,
            action: "natural_stop",
          });
        }
      }
    }
  }, [pagePlayData.pageTimer]);

  useEffect(() => {
    if (pagePlayData.action === "play" && pagePlayData.pageProgress <= 100) {
      let timer = pagePlayData?.pageTimer ?? 0;
      const { pageId, pageIdx, pageObj, pageDurationMs } = playerHelper.getPageData();
      if (!pageId) {
        console.log("failure", pageIdx);
        return;
      }

      playerHelper.resumeDomPlayEnv({ timer, pageDurationMs, pageId });

      if (timer === 0) {
        playerHelper.pagePlay({ timer: 0, pageDurationMs, pageObj, pageId, pageIdx });
        playerHelper.widgetPlay({ timer: 0, pageDurationMs, pageId });
      }

      if (intervalId.current) clearInterval(intervalId.current);

      intervalId.current = setInterval(() => {
        timer += 50;
        playerHelper.widgetPlay({ timer, pageDurationMs, pageId });
        playerHelper.pagePlay({ timer, pageDurationMs, pageObj, pageId, pageIdx });
      }, 50);
    }

    if (pagePlayData.action === "pause") {
      playerHelper.stopDomPlay();
    }

    if (pagePlayData.action === "natural_stop") {
      playerHelper.stopDomPlay();
      setPagePlayData({
        action: null,
        pageId: null,
        pageIdx: null,
        pageTimer: null,
        pageProgress: 0,
        pageDurationMs: 0,
      });
    }

    return () => {
      playerHelper.stopDomPlay();
    };
  }, [pagePlayData.action]);

  // Set video initial screen on first load
  useLayoutEffect(() => {
    if (!ready) return;
    const { pageArray } = playerHelper.getDocumentData();
    if (!pageArray?.length) return;

    const targetPageObj = pageArray[0];
    const firstPageNode = document.getElementById(`dhp-page-${targetPageObj.pageIdx}`);

    // Step 1: Initially hide all pages
    document.querySelectorAll(".present-page").forEach(page => {
      page.classList.add("d-slide-none");
    });

    // Step 2: Collect all media elements (images, videos, audios)
    const allMedia = Array.from(document.getElementById("pagePlay").querySelectorAll("img, video, audio"));
    const totalMedia = allMedia.length;
    let loadedCount = 0;

    const handleMediaLoaded = () => {
      loadedCount++;
      if (loadedCount === totalMedia) {
        // All media loaded, now remove the "d-slide-none" from first page
        firstPageNode?.classList.remove("d-slide-none");
        setIsPlayerReady(true);

        // Initialize playback environment
        playerHelper.setDomPlayEnvToCustomTime({
          pageId: targetPageObj.pageId,
          pageObj: docPagesMeta.pageNodes[0],
          pageDurationMs: targetPageObj.pageDurationMs,
          timer: 0,
          meta: {
            pageIdx: targetPageObj.pageIdx,
            targetPageTimer: 0,
            pageProgress: 0,
            documentProgress: 0,
            documentTimer: 0,
          },
        });

        // playerHelper.preloadMedia();
        requestAnimationFrame(() => {
          playerHelper.togglePlayPause(isPlaying);
          playerHelper.controllVolume(0);
        });
      }
    };

    const onError = media => {
      console.warn("Media failed to load:", media.src);
      handleMediaLoaded(); // Treat failed media as 'loaded' so loader can continue
    };

    // Step 3: Add listeners to all media
    allMedia.forEach(media => {
      const errorHandler = () => onError(media);
      media.addEventListener("error", errorHandler);
      media._onError = errorHandler;

      if (media.tagName === "IMG") {
        if (media.complete) handleMediaLoaded();
        else media.addEventListener("load", handleMediaLoaded);
      } else {
        if (media.readyState >= 3) handleMediaLoaded(); // already loaded enough
        else media.addEventListener("canplaythrough", handleMediaLoaded);
      }
    });

    // Step 4: Handle edge case (no media)
    if (totalMedia === 0) handleMediaLoaded();

    return () => {
      // Cleanup event listeners
      allMedia.forEach(media => {
        media.removeEventListener("load", handleMediaLoaded);
        media.removeEventListener("canplaythrough", handleMediaLoaded);
        if (media._onError) media.removeEventListener("error", media._onError);
      });
    };
  }, [docPagesMeta, ready]);

  return {
    documentPlayData,
    isPlaying,
    isPlayerReady,
    togglePlayPause: playerHelper.togglePlayPause,
    getDocumentData: playerHelper.getDocumentData,
    handleSeek: playerHelper.handleSeek,
    handleMouseDown: playerHelper.handleMouseDown,
    handleMouseUpOrLeave: playerHelper.handleMouseUpOrLeave,
    controllVolume: playerHelper.controllVolume,
  };
};

export default useHTMLPlayer;
